import { FormField } from "@components/forms/FormField";
import { cn } from "@lib/utils/cn";
import { type Control, Controller } from "react-hook-form";

interface RadioOption {
  value: string;
  label: string;
  description?: string;
}

interface RadioGroupFieldProps {
  name: string;
  control: Control<any>;
  label: string;
  options: RadioOption[];
  /** Render variant: "radio" (default) or "chips" (badge buttons) */
  variant?: "radio" | "chips";
  /** Layout direction */
  direction?: "horizontal" | "vertical";
  disabled?: boolean;
  required?: boolean;
  hint?: string;
}

export function RadioGroupField({
  name,
  control,
  label,
  options,
  variant = "radio",
  direction = "horizontal",
  disabled = false,
  required = false,
  hint,
}: RadioGroupFieldProps) {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: "Ce champ est requis" } : undefined}
      render={({ field, fieldState }) => (
        <FormField label={label} error={fieldState.error?.message} hint={hint}>
          {variant === "chips" ? (
            <div
              role="radiogroup"
              aria-label={label}
              className={cn("flex gap-2 flex-wrap", direction === "vertical" && "flex-col")}
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={field.value === option.value}
                  className={cn(
                    "badge transition-all duration-200 cursor-pointer active:scale-95 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none",
                    field.value === option.value ? "badge-primary" : "badge-outline",
                    disabled && "opacity-50 cursor-not-allowed",
                  )}
                  onClick={() => !disabled && field.onChange(option.value)}
                  disabled={disabled}
                  title={option.description}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : (
            <div
              className={cn(
                "flex gap-4",
                direction === "vertical" ? "flex-col" : "flex-row flex-wrap",
              )}
            >
              {options.map((option) => (
                <label key={option.value} className="label cursor-pointer justify-start gap-3">
                  <input
                    type="radio"
                    value={option.value}
                    checked={field.value === option.value}
                    onChange={field.onChange}
                    disabled={disabled}
                    className="radio radio-primary transition-all duration-200"
                  />
                  <span className="label-text">{option.label}</span>
                </label>
              ))}
            </div>
          )}
        </FormField>
      )}
    />
  );
}
